import React, { useState, useRef } from "react";
import { Box, LinearProgress, IconButton } from "@mui/material";
import { CloudUpload, Delete, CheckCircle } from "@mui/icons-material";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import { compcheckApi } from "configs/api";

interface FileUploadProps {
  type: "video" | "image" | "audio" | "image_audio";
  onUploadComplete: (
    url: string | { image: string; audio: string },
    metadata?: { durationSeconds?: number }
  ) => void;
  existingUrl?: string;
  label?: string;
  accept?: string;
}

function FileUpload({
  type,
  onUploadComplete,
  existingUrl,
  label,
  accept,
}: FileUploadProps): React.JSX.Element {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedUrl, setUploadedUrl] = useState<string | { image: string; audio: string } | null>(
    existingUrl ? (typeof existingUrl === "string" ? existingUrl : null) : null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  const getUploadedFileUrl = (responseData: any): string => {
    return responseData?.result || responseData?.path || responseData?.data?.url || "";
  };

  const getVideoDurationSeconds = (file: File): Promise<number | undefined> => {
    return new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const video = document.createElement("video");

      const cleanup = () => {
        URL.revokeObjectURL(objectUrl);
        video.removeAttribute("src");
        video.load();
      };

      video.preload = "metadata";
      video.onloadedmetadata = () => {
        const seconds = Number.isFinite(video.duration)
          ? Math.max(0, Math.floor(video.duration))
          : undefined;
        cleanup();
        resolve(seconds);
      };
      video.onerror = () => {
        cleanup();
        resolve(undefined);
      };
      video.src = objectUrl;
    });
  };

  const getAudioDurationSeconds = (file: File): Promise<number | undefined> => {
    return new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const audio = document.createElement("audio");

      const cleanup = () => {
        URL.revokeObjectURL(objectUrl);
        audio.removeAttribute("src");
        audio.load();
      };

      audio.preload = "metadata";
      audio.onloadedmetadata = () => {
        const seconds = Number.isFinite(audio.duration)
          ? Math.max(0, Math.floor(audio.duration))
          : undefined;
        cleanup();
        resolve(seconds);
      };
      audio.onerror = () => {
        cleanup();
        resolve(undefined);
      };
      audio.src = objectUrl;
    });
  };

  const getAcceptTypes = () => {
    switch (type) {
      case "video":
        return "video/mp4,video/webm,video/ogg,video/quicktime";
      case "image":
        return "image/jpeg,image/png,image/gif,image/webp";
      case "audio":
        return "audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm";
      default:
        return accept || "";
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setProgress(0);

    try {
      let metadata: { durationSeconds?: number } | undefined;
      if (type === "video" || type === "audio") {
        const durationSeconds = type === "video"
          ? await getVideoDurationSeconds(file)
          : await getAudioDurationSeconds(file);
        metadata =
          typeof durationSeconds === "number"
            ? {
                durationSeconds,
              }
            : undefined;
      }

      const formData = new FormData();
      formData.append("image", file);

      const response = await compcheckApi.post("/api/file/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
          }
        },
      });

      if (response.data) {
        const url = getUploadedFileUrl(response.data);
        if (!url) {
          throw new Error("Upload response did not include a file URL");
        }
        setUploadedUrl(url);
        onUploadComplete(url, metadata);
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleImageAudioUpload = async () => {
    const imageFile = imageInputRef.current?.files?.[0];
    const audioFile = audioInputRef.current?.files?.[0];

    if (!imageFile || !audioFile) {
      alert("Please select both image and audio files");
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const imageFormData = new FormData();
      imageFormData.append("image", imageFile);

      const imageResponse = await compcheckApi.post("/api/file/upload", imageFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
          }
        },
      });

      const audioFormData = new FormData();
      audioFormData.append("image", audioFile);

      const audioResponse = await compcheckApi.post("/api/file/upload", audioFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const imageUrl = getUploadedFileUrl(imageResponse.data);
      const audioUrl = getUploadedFileUrl(audioResponse.data);

      if (imageUrl && audioUrl) {
        const urls = {
          image: imageUrl,
          audio: audioUrl,
        };
        setUploadedUrl(urls);
        onUploadComplete(urls);
      } else {
        throw new Error("Upload response did not include file URLs");
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      if (imageInputRef.current) imageInputRef.current.value = "";
      if (audioInputRef.current) audioInputRef.current.value = "";
    }
  };

  const handleRemove = () => {
    setUploadedUrl(null);
    onUploadComplete("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (audioInputRef.current) audioInputRef.current.value = "";
  };

  if (type === "image_audio") {
    return (
      <Box>
        {label && (
          <Typography variant="caption" fontWeight="medium" mb={1} display="block">
            {label}
          </Typography>
        )}
        <Box display="flex" flexDirection="column" gap={2}>
          <Box>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              style={{ display: "none" }}
            />
            <Button
              variant="outlined"
              color="info"
              size="small"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploading}
            >
              <CloudUpload sx={{ mr: 1 }} /> Select Image
            </Button>
            {imageInputRef.current?.files?.[0] && (
              <Typography variant="caption" ml={1}>
                {imageInputRef.current.files[0].name}
              </Typography>
            )}
          </Box>
          <Box>
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm"
              style={{ display: "none" }}
            />
            <Button
              variant="outlined"
              color="info"
              size="small"
              onClick={() => audioInputRef.current?.click()}
              disabled={uploading}
            >
              <CloudUpload sx={{ mr: 1 }} /> Select Audio
            </Button>
            {audioInputRef.current?.files?.[0] && (
              <Typography variant="caption" ml={1}>
                {audioInputRef.current.files[0].name}
              </Typography>
            )}
          </Box>
          <Button
            variant="gradient"
            color="info"
            size="small"
            onClick={handleImageAudioUpload}
            disabled={uploading || !imageInputRef.current?.files?.[0] || !audioInputRef.current?.files?.[0]}
            loading={uploading}
          >
            Upload Both
          </Button>
        </Box>
        {uploading && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />}
        {uploadedUrl && typeof uploadedUrl === "object" && (
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" fontSize="small" />
            <Typography variant="caption" color="success">
              Uploaded successfully
            </Typography>
            <IconButton size="small" onClick={handleRemove}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {label && (
        <Typography variant="caption" fontWeight="medium" mb={1} display="block">
          {label}
        </Typography>
      )}
      <Box>
        <input
          ref={fileInputRef}
          type="file"
          accept={getAcceptTypes()}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
          <Button
            variant="outlined"
            color="info"
            size="medium"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <CloudUpload sx={{ mr: 1 }} /> {uploadedUrl ? "Change" : "Upload"}
          </Button>
      </Box>
      {uploading && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />}
    </Box>
  );
}

export default FileUpload;

